import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useApi } from "./useApi";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import * as z from "zod";
import { LogLevel } from "@/components/LogTable";
import { recentEndDateAtom } from "@/atoms/runsFilterAtoms";
import { useAtom } from "jotai";
import { recentStartDateAtom } from "@/atoms/runsFilterAtoms";

const logEntrySchema = z.object({
  installation_id: z.string(),
  record_id: z.string(),
  record_name: z.string(),
  record_type: z.string(),
  record_order: z.number(),
  job_id: z.string().nullable(),
  job_name: z.string().nullable(),
  job_display_name: z.string().nullable(),
  repo: z.string(),
  workflow: z.string(),
  branch: z.string(),
  pull_request_number: z
    .number()
    .transform((val) => (val === 0 ? null : val))
    .nullable(),
  run_id: z.string(),
  run_attempt: z.number(),
  vm_id: z.string(),
  sender: z.string(),
  log_timestamp: z.string().transform((val) => {
    const date = new Date(val);
    // Ensure we parse as UTC by appending Z if not already present
    if (!val.endsWith("Z")) {
      return new Date(val + "Z");
    }
    return date;
  }),
  message: z.string(),
  log_line_number: z.number().transform((x) => x ?? 0),
  level: z
    .string()
    .optional()
    .transform((val) => val ?? "info") as z.ZodType<LogLevel>,
});

export type LogEntry = z.infer<typeof logEntrySchema>;

export type LogCursor = {
  log_timestamp: string;
  log_line_number: number;
  record_id: string;
};

const logSearchResponseSchema = z.object({
  logs: z.array(logEntrySchema),
  next_cursor: z
    .object({
      log_timestamp: z.string(),
      log_line_number: z.number().transform((x) => x ?? 0),
      record_id: z.string(),
    })
    .nullable(),
  has_more_logs: z.boolean().optional(),
});

export type LogSearchResponse = z.infer<typeof logSearchResponseSchema>;

/** Paginated log search over the active org and the selected date range. */
export function useLogSearch(queryString: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  const [allLogs, setAllLogs] = useState<LogEntry[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [nextCursor, setNextCursor] = useState<LogCursor | null>(null);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState<boolean>(false);
  // Discard responses from older searches so they cannot overwrite newer results.
  const queryVersionRef = useRef(0);
  const currentQueryVersion = useRef(0);

  const baseQueryKey = useMemo(
    () => [
      "logSearch",
      activeGithubOrg || "",
      startDate.toISOString(),
      endDate.toISOString(),
      queryString,
    ],
    [activeGithubOrg, startDate, endDate, queryString],
  );

  const fetchNextPage = useCallback(async () => {
    if (!hasNextPage || !nextCursor || !activeGithubOrg || isLoadingNextPage)
      return;

    const nextPageQueryKey = [...baseQueryKey, nextCursor];
    const thisQueryVersion = currentQueryVersion.current;

    try {
      setIsLoadingNextPage(true);

      const existingData =
        queryClient.getQueryData<LogSearchResponse>(nextPageQueryKey);
      if (existingData) {
        if (thisQueryVersion === currentQueryVersion.current) {
          setIsLoadingNextPage(false);
          setAllLogs((prev) => [...prev, ...existingData.logs]);
          setNextCursor(existingData.next_cursor);
          setHasNextPage(
            !!existingData.next_cursor || !!existingData.has_more_logs,
          );
        }
        return;
      }

      const response = await fetchLogSearch(
        api,
        activeGithubOrg,
        { from: startDate, to: endDate },
        queryString,
        nextCursor,
      );

      if (thisQueryVersion === currentQueryVersion.current) {
        const newLogs = response.logs.map((log) => ({
          ...log,
        }));

        setAllLogs((prev) => [...prev, ...newLogs]);
        setNextCursor(response.next_cursor);
        setHasNextPage(!!response.next_cursor || !!response.has_more_logs);

        queryClient.setQueryData<LogSearchResponse>(nextPageQueryKey, {
          ...response,
        });
      }

      return response;
    } catch (error) {
      console.error("Error fetching next page:", error);
      if (thisQueryVersion === currentQueryVersion.current) {
        setHasNextPage(false);
      }
    } finally {
      if (thisQueryVersion === currentQueryVersion.current) {
        setIsLoadingNextPage(false);
      }
    }
  }, [
    activeGithubOrg,
    api,
    baseQueryKey,
    endDate,
    hasNextPage,
    isLoadingNextPage,
    nextCursor,
    queryClient,
    queryString,
    startDate,
  ]);

  // TODO: `allLogs` is reset inside the query fetcher (lines below), but when
  // React Query serves a cached result within the 5-minute staleTime window the
  // fetcher never runs, so `allLogs` keeps showing data from the *previous*
  // filter combination. This causes stale logs to persist when toggling filters
  // back to a previously-visited query string (e.g. select user A, add user B,
  // then deselect user A — the table still shows A+B results). Affects all
  // filter types, not just users. Fix by moving the `allLogs`/`nextCursor`/
  // `hasNextPage` reset into a `useEffect` keyed on `baseQueryKey` so it fires
  // regardless of cache hits.

  const result = useQuery(
    baseQueryKey,
    async () => {
      if (!activeGithubOrg || !startDate || !endDate) return [];

      queryVersionRef.current += 1;
      currentQueryVersion.current = queryVersionRef.current;
      const thisQueryVersion = currentQueryVersion.current;

      setAllLogs([]);
      setHasNextPage(true);
      setNextCursor(null);

      const response = await fetchLogSearch(
        api,
        activeGithubOrg,
        { from: startDate, to: endDate },
        queryString,
      );

      if (thisQueryVersion === currentQueryVersion.current) {
        const formattedLogs = response.logs.map((log) => ({
          ...log,
        }));

        setAllLogs(formattedLogs);
        setNextCursor(response.next_cursor);
        setHasNextPage(!!response.next_cursor || !!response.has_more_logs);

        return formattedLogs;
      }

      // If this is a stale query, return empty array to avoid overwriting newer data
      return [];
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  return {
    ...result,
    data: allLogs.length > 0 ? allLogs : result.data || [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage: isLoadingNextPage,
  };
}

export async function fetchLogSearch(
  api: ReturnType<typeof useApi>,
  org: string,
  dateRange: { from: Date; to: Date },
  query: string,
  cursor?: LogCursor,
) {
  const params = new URLSearchParams();

  params.append("start_time", dateRange.from.toISOString());
  params.append("end_time", dateRange.to.toISOString());
  params.append("query", query);

  if (cursor) {
    params.append("cursor_timestamp", cursor.log_timestamp);
    params.append("cursor_record_id", cursor.record_id);
    params.append("cursor_log_line_number", cursor.log_line_number.toString());
  }

  const response = await api.get(
    `user/github/orgs/${org}/metrics/logs/search?${params.toString()}`,
  );

  return logSearchResponseSchema.parse(response.data);
}

const filterOptionSchema = z.object({
  value: z.string(),
  present: z.boolean(),
});

const filterOptionsSchema = z.object({
  branches: z.array(filterOptionSchema).optional(),
  senders: z.array(filterOptionSchema).optional(),
  repos: z.array(filterOptionSchema).optional(),
  workflows: z.array(filterOptionSchema).optional(),
  levels: z
    .array(filterOptionSchema)
    .optional()
    .transform((x) => x ?? []),
  job_names: z.array(filterOptionSchema).optional(),
  step_names: z.array(filterOptionSchema).optional(),
});

export type LogSearchFilterOptionsProperty =
  | "workflows"
  | "job_names"
  | "step_names"
  | "branches"
  | "senders"
  | "repos";

export function useLogSearchFilterOptions(
  property: LogSearchFilterOptionsProperty,
  enabled: boolean = true,
) {
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  const optionsQueryKey = useMemo(
    () => [
      "logSearchFilterOptions",
      activeGithubOrg,
      startDate,
      endDate,
      property,
    ],
    [activeGithubOrg, startDate, endDate, property],
  );

  const queryResult = useQuery(
    optionsQueryKey,
    async () => {
      if (!activeGithubOrg) {
        return {
          levels: [],
          branches: [],
          senders: [],
          repos: [],
          workflows: [],
          job_names: [],
          step_names: [],
        };
      }

      return await fetchLogSearchFilterOptions(
        api,
        activeGithubOrg,
        {
          from: startDate,
          to: endDate,
        },
        property,
      );
    },
    {
      enabled: !!activeGithubOrg && enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  return queryResult;
}

export async function fetchLogSearchFilterOptions(
  api: ReturnType<typeof useApi>,
  org: string,
  dateRange: { from: Date; to: Date },
  property: LogSearchFilterOptionsProperty,
) {
  const response = await api.get(
    `user/github/orgs/${org}/metrics/logs/search/filter-options?start_time=${dateRange.from.toISOString()}&end_time=${dateRange.to.toISOString()}&property=${property}`,
  );

  return filterOptionsSchema.parse(response.data);
}

const histogramSchema = z.object({
  buckets: z.array(
    z.object({
      start: z.string(),
      end: z.string(),
      log_count: z.number(),
      error_count: z.number(),
      warn_count: z.number(),
      info_count: z.number(),
      debug_count: z.number(),
    }),
  ),
  meta: z.object({
    total_count: z.number(),
  }),
});

export async function fetchLogSearchHistogram(
  api: ReturnType<typeof useApi>,
  org: string,
  query: string,
  dateRange: { from: Date; to: Date },
) {
  const response = await api.get(
    `user/github/orgs/${org}/metrics/logs/histogram?query=${encodeURIComponent(
      query,
    )}&start_time=${dateRange.from.toISOString()}&end_time=${dateRange.to.toISOString()}`,
  );

  return histogramSchema.parse(response.data);
}

export const useLogSearchHistogram = (query: string) => {
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  const queryKey = useMemo(
    () => [
      "logSearchHistogram",
      activeGithubOrg,
      query,
      startDate.toISOString(),
      endDate.toISOString(),
    ],
    [activeGithubOrg, query, startDate, endDate],
  );

  return useQuery(
    queryKey,
    async () => {
      if (!activeGithubOrg) return { buckets: [], meta: { total_count: 0 } };

      const response = await fetchLogSearchHistogram(
        api,
        activeGithubOrg,
        query,
        { from: startDate, to: endDate },
      );

      return response;
    },
    {
      suspense: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};
